import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import DoctorIcon from '@/components/ui/icons/Doctor'
import PatientIcon from '@/components/ui/icons/Patient'
import { Expertise } from '@/core/models/expertise.model'
import CalendarIcon from '@/components/ui/icons/Calendar'
import Pagination from '@/components/ui/pagination/Pagination'

export type ExpertisesCardViewProps = {
  data: Expertise[]
  onRowsPerPageChange?: (rowsPerPage: number) => void
  onPageChange?: (newPage: number) => void
  rowsPerPageOptions?: number[]
  totalCount: number
  isDoctor: boolean
}

const ExpertisesCardView = (props: ExpertisesCardViewProps) => {
  const { data, onRowsPerPageChange, onPageChange, rowsPerPageOptions, totalCount, isDoctor } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [page, setPage] = useState<number>(1)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={page}
          onPageChange={handleChangePage}
          onItemsPerPageChange={onRowsPerPageChange}
          totalItems={totalCount}
        />
      </Box>
      <Grid container spacing={2}>
        {data.map((row) => (
          <Grid item xs={12} sm={6} md={4} key={row._id}>
            <Card onClick={() => navigate(`/expertises/${row._id}`)} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center">
                        <CalendarIcon fontSize="small" style={{ marginRight: '8px' }} />
                        <Chip
                          color="secondary"
                          label={
                            <span>
                              {dayjs(row.startDate).isSame(dayjs(), 'day') ? t(`dayjs:calendar:today`) : dayjs(row.startDate).format('DD/MM/YYYY')} -{' '}
                              <b>{dayjs(row.startDate).format('HH[h]mm')}</b>
                            </span>
                          }
                          variant="filled"
                        />
                      </Box>
                      <Chip
                        color={row.status === 'draft' ? 'secondary' : row.status === 'sent' ? 'success' : 'warning'}
                        label={t(`common:model.expertise.status.${row.status}.label`, { context: isDoctor ? 'doctor' : 'pds' })}
                        size="small"
                        variant="filled"
                      />
                    </Box>
                  </Grid>
                  <Grid item>
                    <DoctorIcon isFemale={row.medecin?.userInfo.gender === 'F'} />
                  </Grid>
                  <Grid item>
                    <Typography>{`${row.medecin?.userInfo.name} ${row.medecin?.userInfo.surname}`}</Typography>
                  </Grid>
                </Grid>
                <Grid container direction="row" alignItems="center" spacing={1}>
                  <Grid item>
                    <PatientIcon isFemale={row.patient.userInfo.gender === 'F'} />
                  </Grid>
                  <Grid item>
                    <Typography>{`${row.patient.userInfo.name} ${row.patient.userInfo.surname}`}</Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="column" justifyContent="end" spacing={1}>
                    <Button variant="contained" color="primary" onClick={() => navigate(`/expertises/${row._id}`)}>
                      {t('page.expertises.openExpertiseButton.tooltip')}
                    </Button>
                  </Stack>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default ExpertisesCardView
